// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState, xHeaderActions, xHeaderState } from "@bg/App/Store/Reducers";
import { styledScrollbar, xScreen } from "@bg/XDL/Utilities";
import AutocompleteProducts, { AutocompleteProductsStyles } from "@bgs/Overrides/Widgets/Header/AutocompleteProducts";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteSuggestions, { AutocompleteSuggestionsStyles } from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import SearchHistory, { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";
import Clickable from "@insite/mobius/Clickable";
import { FormFieldIcon } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Page from "@insite/mobius/Page";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);

    const customSettings = getSettingsCollection(state).customSettings as any;
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        isSticky: state.xHeader.isSticky,
        searchInputFocus: state.xHeader.searchInputFocus,
        searchPath,
        customSettings,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    // XNG-Changes
    setSearchInputStatus: (focus: boolean) => (dispatch: Dispatch<xHeaderActions>) => {
        dispatch({ type: "xHeader/searchInputFocus", focus });
    },
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    autocompleteColumnWrapper1?: InjectableCss;
    autocompleteColumnWrapper2?: InjectableCss;
    wrapper?: InjectableCss;
    searchInputCtasWrapper?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: {
            src: "Search",
            css: css`
                top: 5px !important;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
                input {
                    height: 27px !important;
                    background: ${getColor("text.main")} !important;
                    color: ${getColor("common.white")} !important;
                    border: 0 !important;
                    border-radius: 0 !important;
                    border-bottom: 1px solid ${getColor("common.ui12")} !important;
                    padding: 0 10px 8px 30px !important;
                    min-width: 95px;
                    &:focus {
                        padding-right: 80px !important;
                    }

                    ${({ searchInputFocus }: any) =>
                        searchInputFocus &&
                        css`
                            padding-right: 115px;
                        `}
                }
                input::placeholder {
                    color: ${getColor("common.ui13")} !important;
                    opacity: 1 !important;
                    font-size: 14px;
                    font-weight: 400;
                    letter-spacing: 0;
                    line-height: 16px;
                }
                ${FormFieldIcon} {
                    max-width: unset;
                    max-height: unset;
                }
                svg {
                    width: 15px;
                    height: 15px;
                    fill: ${getColor("common.white")} !important;
                }

                button {
                    left: 3px;
                    top: -6px !important;
                    left: -10px !important;
                    color: ${getColor("common.white")} !important;
                    svg {
                        color: ${getColor("common.white")};
                    }
                }
            `,
        },
    },
    popover: {
        transitionDuration: "short",
        toggle: false,
        css: css`
            box-shadow: 0px 300px 19px 0px rgb(0 0 0 / 26%);
        `,
    } as any,
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: center;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            height: 687px;
            min-height: 390px;
            overflow-x: hidden;
            overflow-y: auto;
            ${styledScrollbar(6)};

            max-height: calc(100vh - 140px);

            ${({ isSticky }: any) =>
                isSticky &&
                css`
                    max-height: calc(100vh - 95px);
                `};

            ${xScreen("lte", "md")} {
                flex-direction: column;
                max-height: 60vh;
            }
        `,
    },
    autocompleteColumnWrapper1: {
        css: css`
            width: 359px;
            padding-top: 38px;
            padding-bottom: 34px;
            padding-right: 20px;
            background-color: ${getColor("common.ui4")};

            &:not(.split-two-views) {
                width: 100%;
                padding-left: 0;
                border-right: 0;
                border: 0;
            }

            ${xScreen("lte", "md")} {
                width: 100%;
                padding-right: 0;
                padding-bottom: 20px;
                border-right: 0;

                &:not(.split-two-views) {
                    padding-bottom: 0;
                    border-bottom: 0;
                }
            }
        `,
    },
    autocompleteColumnWrapper2: {
        css: css`
            width: calc(100% - 359px);
            padding-top: 38px;
            padding-bottom: 34px;
            padding-left: 40px;
            padding-right: 26px;

            &:not(.split-two-views) {
                width: 100%;
                padding-left: 26px;
            }

            ${xScreen("lte", "md")} {
                width: 100%;
                padding-left: 0;
                padding-top: 20px;

                &:not(.split-two-views) {
                    border-top: 0;
                }
            }
        `,
    },
    wrapper: {},
    searchInputCtasWrapper: {
        css: css`
            display: block;
            width: 100%;
            position: relative;
            max-width: 666px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
            }

            .search-ctas {
                display: none;
                ${({ searchInputFocus }: any) =>
                    searchInputFocus &&
                    css`
                        display: flex;
                    `}
            }
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;
    searchHistory = getSearchHistory();

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (
                this.props.location.pathname &&
                !this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)
            ) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        }
        // XNG-Changes
        else if (this.props.customSettings?.attributeAutoFilter) {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(
                `/${this.props.searchPath}?query=${encodedQuery}&attributeValueIds=${encodeURIComponent(this.props.customSettings.attributeAutoFilter)}`,
                false,
            );
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }

        this.inputFocusStoreReset();
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState({
                        autoCompleteModel: result,
                    });
                    this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.showAutocomplete = true;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };

    openAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    closeAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        const headerElement = document.getElementsByTagName("header")[0].getBoundingClientRect();
        const windowSize = window.innerWidth;

        if (element.current) {
            const rect = element.current.getBoundingClientRect();

            if (this.props.isSticky) {
                positionStyle.top = headerElement.height;
            } else {
                positionStyle.top = headerElement.height + headerElement.top - 55;
            }
            positionStyle.right = 0;
            positionStyle.width = "100%";
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY: {
                this.moveFocus("next");
                break;
            }
            case UP_KEY: {
                this.moveFocus("prev");
                break;
            }
            case ENTER_KEY: {
                const searchId = `headerSearch-${this.props.id}`;
                document?.getElementById(searchId)?.blur();
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            }
            default: {
                break;
            }
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };
    goToUrl = (url: string, track = true) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();

        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    inputFocusSet = (event?: any) => {
        this.props.setSearchInputStatus(true);
    };

    inputFocusReset = (event?: any) => {
        const nativeElementClassList = event.nativeEvent?.relatedTarget?.classList;
        if (
            event &&
            (!event.target.id.includes("headerSearch") ||
                (event.target.id.includes("headerSearch") &&
                    event.type === "blur" &&
                    !(
                        event.nativeEvent?.relatedTarget?.classList?.contains("search-backspace") ||
                        event.nativeEvent?.relatedTarget?.classList?.contains("search-cancel") ||
                        event.relatedTarget?.classList?.contains("clickable-icon")
                    )))
        ) {
            this.inputFocusStoreReset();
        }
    };

    inputFocusStoreReset = () => {
        setTimeout(() => {
            this.props.setSearchInputStatus(false);
        }, 100);
    };

    executeBackspace = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        const newQuery = this.state.query.slice(0, -1);
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };
    executeCancelSearch = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ query: "" });
        this.inputFocusStoreReset();
        this.handlePopoverClose();
        this.closeAutocomplete();
    };

    closeAutoCompleteAndPopover = () => {
        this.handlePopoverClose();
        this.closeAutocomplete();
        this.showAutocomplete = false;
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            brands,
            content,
            products,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);

        if (!resultCount) {
            return null;
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteColumnWrapper1}
                            className={products && products.length > 0 ? "split-two-views" : ""}
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}
                {!isRetailSearchCompletionResults && (
                    <StyledWrapper
                        {...this.styles.autocompleteWrapper}
                        {...({ isSticky: this.props.isSticky } as any)}
                        className="autocomplete-wrapper"
                    >
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper1}
                                className={`autocomplete-column-wrapper ${
                                    products && products.length > 0 ? "split-two-views" : ""
                                }`}
                            >
                                <Page
                                    as="div"
                                    css={css`
                                        padding-right: 0 !important;
                                    `}
                                >
                                    <AutocompleteCategories
                                        categories={categories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    />
                                    <AutocompleteBrands
                                        brands={brands}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteBrandsStyles}
                                    />
                                    <AutocompleteContent
                                        content={content}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteContentStyles}
                                    />
                                </Page>
                            </StyledWrapper>
                        )}
                        {products && products.length > 0 && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper2}
                                className={`autocomplete-column-wrapper ${
                                    (categories && categories.length > 0) ||
                                    (brands && brands.length > 0) ||
                                    (content && content.length > 0)
                                        ? "split-two-views"
                                        : ""
                                }`}
                            >
                                <Page
                                    as="div"
                                    css={css`
                                        padding-left: 0 !important;
                                    `}
                                >
                                    <AutocompleteProducts
                                        products={products}
                                        goToUrl={this.goToUrl}
                                        viewAll={this.doSearch}
                                    />
                                </Page>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        const searchInputFocus = this.props.searchInputFocus;
        const searchInput = (
            <StyledWrapper {...({ searchInputFocus } as any)} {...styles.searchInputCtasWrapper}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    iconClickableProps={{
                        onClick: () => {
                            // apply focus state when clicking on search icon
                            this.inputFocusSet();
                            this.doSearch();
                        },
                        className: "clickable-icon",
                        css: css`
                            &:hover {
                                span {
                                    color: ${getColor("text.link")};
                                    svg {
                                        fill: ${getColor("text.link")};
                                    }
                                }
                            }
                        `,
                    }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    onFocus={this.inputFocusSet}
                    onBlur={this.inputFocusReset}
                    placeholder={translate("What can we help you find?")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    {...({ searchInputFocus } as any)}
                />
                <StyledWrapper
                    className="search-ctas"
                    css={css`
                        display: flex;
                        align-items: center;
                        position: absolute;
                        top: 50%;
                        right: 2px;
                        transform: translatey(-50%);
                        z-index: 2;
                        color: ${getColor("common.white")} !important;
                        button {
                            color: ${getColor("common.white")} !important;
                        }
                        svg {
                            fill: ${getColor("common.white")} !important;
                        }
                    `}
                >
                    <Clickable
                        aria-label={translate("delete")}
                        onClick={this.executeCancelSearch}
                        stopPropagation={true}
                        className="search-backspace"
                        css={css`
                            text-transform: uppercase;
                            font-size: 12px;
                            font-weight: 500;
                            letter-spacing: 0.5px;
                            line-height: 16px;
                            margin-right: 6px;
                        `}
                    >
                        {translate("Clear")}
                        <Icon
                            src="Clear"
                            color="white"
                            size={8}
                            css={css`
                                margin-left: 6px;
                            `}
                        ></Icon>
                    </Clickable>
                </StyledWrapper>
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={searchInput}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={this.handlePopoverClose}
                    className="popover"
                >
                    {!this.state.query && this.showAutocomplete && this.searchHistory?.length > 0 && (
                        <Page as="div">
                            <StyledWrapper {...styles.wrapper}>
                                <StyledWrapper {...this.styles.autocompleteWrapper}>
                                    <SearchHistory
                                        focusedQuery={this.state.focusedSearchHistoryQuery}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={styles.searchHistoryStyles}
                                        {...({ closeAutoCompleteAndPopover: this.closeAutoCompleteAndPopover } as any)}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        </Page>
                    )}
                    {this.state.query && this.renderAutocomplete()}
                </Popover>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
